import { useCallback } from 'react'
import { connect } from 'react-redux'

import {
  convertOccupancies,
} from './utils'

import {
  tripLoginModalSignUpDismiss,
  tripLoginModalSignUpView,
} from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import {
  buildSuggestedDatesParamsKey,
  parseOccupancy,
} from 'lib/search/searchUtils'
import { parseSearchString } from 'lib/url/searchUrlUtils'
import type { BookmarkDatesAndOccupancy } from 'wishlists/types'
import { useBedbankWishlistDisplayFields } from 'wishlists/hooks/useWishlistDisplayFields'
import WishlistOfferBookmarkButton from 'wishlists/components/WishlistOfferBookmarkButton'

interface Props extends Utils.PropsWithDataAttributes {
  offer: App.BedbankOffer | App.BedbankOfferSummary
  datesAndGuests: BookmarkDatesAndOccupancy
  roomTypeId?: string
  withLabel?: React.ComponentProps<typeof WishlistOfferBookmarkButton>['withLabel']
  size?: React.ComponentProps<typeof WishlistOfferBookmarkButton>['size']
  iconKind?: React.ComponentProps<typeof WishlistOfferBookmarkButton>['iconKind']
  iconVariant?: React.ComponentProps<typeof WishlistOfferBookmarkButton>['iconVariant']
}

interface MappedStateProps {
  suggestedDatesByOfferId: { [offerId: string]: App.OfferSuggestedDates }
}

function BedbankBaseBookmarkButton({
  offer,
  datesAndGuests,
  roomTypeId,
  suggestedDatesByOfferId,
  withLabel,
  size,
  iconKind,
  iconVariant,
  ...rest
}: Props & MappedStateProps) {
  const wishlistDisplayFields = useBedbankWishlistDisplayFields(offer)
  const suggestedDates = suggestedDatesByOfferId?.[offer.id]
  const checkIn =
    datesAndGuests.checkInDate?.format(ISO_DATE_FORMAT) ??
    suggestedDates?.checkIn
  const checkOut =
    datesAndGuests.checkOutDate?.format(ISO_DATE_FORMAT) ??
    suggestedDates?.checkOut

  const trackTripLoginModalSignupView = useCallback(() => {
    fireInteractionEvent(
      tripLoginModalSignUpView(roomTypeId === undefined ? 'save' : 'save_room'),
    )
  }, [roomTypeId])
  const trackTripLoginModalSignupDismiss = useCallback(() => {
    fireInteractionEvent(
      tripLoginModalSignUpDismiss(
        roomTypeId === undefined ? 'save' : 'save_room',
      ),
    )
  }, [roomTypeId])

  return (
    <WishlistOfferBookmarkButton
      {...rest}
      item={{
        type: offer.type,
        code: offer.id,
        roomTypeId,
        startDate: checkIn,
        endDate: checkOut,
        occupancies: convertOccupancies(datesAndGuests.occupancies),
      }}
      {...wishlistDisplayFields}
      withLabel={withLabel}
      size={size}
      iconKind={iconKind}
      iconVariant={iconVariant}
      labelVariant={roomTypeId ? 'room' : 'default'}
      onShowLogin={trackTripLoginModalSignupView}
      onDismissLogin={trackTripLoginModalSignupDismiss}
    />
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>(
  (appState) => {
    const searchString = appState.router.location.search
    const occupancies = parseOccupancy(searchString)
    const { flexibleMonths, flexibleNights } = parseSearchString(searchString)
    const flexibleSearchFilterKey = buildSuggestedDatesParamsKey(
      flexibleMonths,
      flexibleNights,
      occupancies,
    )

    return {
      suggestedDatesByOfferId:
        appState.offer.searchResultMetadata.offerSuggestedDates?.[
          flexibleSearchFilterKey
        ],
    }
  },
)(BedbankBaseBookmarkButton)
